import { useState, useEffect, useRef } from "react";
import useTheme from "../components/useTheme";

import { ReactComponent as Logo } from '../assets/kcg.svg';
import { ReactComponent as Logon } from '../assets/kcgn.svg';

const STARS_COUNT = 130;
const PARTICLES_COUNT = 18;

function Stars() {
  const stars = useRef(
    Array.from({ length: STARS_COUNT }, () => ({
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 2.4 + 0.5,
      delay: Math.random() * 6,
      duration: Math.random() * 2 + 2,
      d: 2 + Math.random() * 4,
      dl: Math.random() * 6,
    }))
  ).current;

  return (
    <div className="stars">
      {stars.map((s, i) => (
        <div
          key={i}
          className="star"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            "--d": `${s.d}s`,
            "--dl": `${s.dl}s`,
          }}
        />
      ))}
    </div>
  );
}

function Particles() {
  const particles = useRef(
    Array.from({ length: PARTICLES_COUNT }, (_, i) => ({
      id: i,
      size: Math.random() * 6 + 3,
      left: Math.random() * 100,
      bottom: Math.random() * 40,
      duration: Math.random() * 8 + 6,
      delay: Math.random() * 8,
      pd: Math.random() * 8 + 6,
      pdl: Math.random() * 8,
    }))
  ).current;

  return (
    <div className="particles">
      {particles.map((p) => (
        <div
          key={p.id}
          className="particle"
          style={{
            width: `${p.size}px`,
            height: `${p.size}px`,
            bottom: `${p.bottom}%`,
            left: `${p.left}%`,
            background: "rgba(93,190,106,0.3)",
            "--pd": `${p.pd}s`,
            "--pdl": `${p.pdl}s`,
          }}
        />
      ))}
    </div>
  );
}

function Clouds() {
  const clouds = [
    { top: "6%", w: "190px", h: "55px", cd: "32s", cdl: "0s" },
    { top: "15%", w: "130px", h: "40px", cd: "48s", cdl: "-18s" },
    { top: "4%", w: "220px", h: "65px", cd: "58s", cdl: "-28s" },
    { top: "22%", w: "100px", h: "32px", cd: "38s", cdl: "-8s" },
  ];

  return (
    <div className="cloud-layer">
      {clouds.map((c, i) => (
        <div
          key={i}
          className="cloud"
          style={{
            top: c.top,
            width: c.w,
            height: c.h,
            "--cd": c.cd,
            "--cdl": c.cdl,
            left: "-15%",
          }}
        />
      ))}
    </div>
  );
}

// Toast types config
const TOAST_TYPES = {
  error: { bg: "#ef4444", icon: "❌" },
  warning: { bg: "#f59e0b", icon: "⚠️" },
  success: { bg: "#22c55e", icon: "✅" },
  info: { bg: "#3b82f6", icon: "ℹ️" },
};

function ToastStack({ toasts }) {
  return (
    <div className="toast-stack">
      {toasts.map((t) => (
        <div key={t.id} className={`toast-item toast-${t.type} ${t.exiting ? "toast-exit" : "toast-enter"}`}>
          <span className="toast-icon">{TOAST_TYPES[t.type].icon}</span>
          <span className="toast-msg">{t.msg}</span>
        </div>
      ))}
    </div>
  );
}

function BuildingScene({ dark }) {
  const setDarkBuilding = (dark) => {
    return dark ? 
      { ground: "#142014", water: "#0a1530", windowColor: "#ffe082" } :
      { ground: "#66bb6a", water: "#5bb8e8", windowColor: "#1a237e" };
  };

  const colors = setDarkBuilding(dark);

  return (
    <svg viewBox="0 0 480 230" xmlns="http://www.w3.org/2000/svg" className="building-scene">
      <ellipse cx="240" cy="205" rx="262" ry="32" fill={colors.ground} />
      <ellipse cx="240" cy="224" rx="262" ry="14" fill={colors.water} opacity="0.75" />
      
      <rect x="72" y="95" width="336" height="118" rx="3" fill="#e53935"/>
      <rect x="72" y="142" width="336" height="8" fill="rgba(255,255,255,0.3)"/>
      
      {[92, 116, 140, 164, 290, 314, 338, 362].map((x) => (
        <rect key={`col-${x}`} x={x} y="112" width="14" height="101" rx="3" fill="#fff"/>
      ))}
      
      <rect x="192" y="46" width="96" height="167" rx="3" fill="#c62828"/>
      <rect x="192" y="58" width="96" height="8" fill="#fff" opacity="0.6"/>
      <path d="M192,46 Q240,14 288,46" fill="#b71c1c" stroke="none"/>
      <rect x="215" y="46" width="50" height="6" rx="2" fill="rgba(255,255,255,0.4)"/>
      
      {[218, 262].map((cx) => (
        <g key={`clock-${cx}`}>
          <circle cx={cx} cy="80" r="15" fill="#fff" stroke="#c62828" strokeWidth="2.5"/>
          <line x1={cx} y1="69" x2={cx} y2="80" stroke="#555" strokeWidth="2" strokeLinecap="round"/>
          <line x1={cx} y1="80" x2={cx + 8} y2="83" stroke="#555" strokeWidth="2" strokeLinecap="round"/>
          <circle cx={cx} cy="80" r="2" fill="#555"/>
        </g>
      ))}
      
      {[80, 110, 140].map((x) => (
        <rect key={`wl-${x}`} x={x} y="103" width="22" height="16" rx="3" fill={colors.windowColor} opacity={dark ? "0.85" : "0.7"}/>
      ))}
      {[298, 328, 358].map((x) => (
        <rect key={`wr-${x}`} x={x} y="103" width="22" height="16" rx="3" fill={colors.windowColor} opacity={dark ? "0.85" : "0.7"}/>
      ))}
      
      <rect x="205" y="110" width="18" height="24" rx="3" fill="#90caf9" opacity="0.7"/>
      <rect x="257" y="110" width="18" height="24" rx="3" fill="#90caf9" opacity="0.7"/>
      <rect x="205" y="148" width="18" height="20" rx="3" fill="#90caf9" opacity="0.5"/>
      <rect x="257" y="148" width="18" height="20" rx="3" fill="#90caf9" opacity="0.5"/>
      
      <rect x="224" y="178" width="32" height="35" rx="4" fill="#8d1a1a"/>
      <rect x="226" y="180" width="28" height="33" rx="3" fill="#a31515" opacity="0.8"/>
      <circle cx="239" cy="196" r="2" fill="#ffcc80"/>
      
      <rect x="402" y="116" width="9" height="82" rx="4" fill="#5d4037"/>
      <rect x="399" y="104" width="9" height="30" rx="3" fill="#5d4037" transform="rotate(-15 399 104)"/>
      <ellipse cx="406" cy="108" rx="30" ry="13" fill="#388e3c" transform="rotate(-12 406 108)"/>
      <ellipse cx="406" cy="105" rx="24" ry="10" fill="#43a047" transform="rotate(10 406 105)"/>
      <ellipse cx="406" cy="102" rx="18" ry="8" fill="#66bb6a" transform="rotate(-5 406 102)"/>
      
      <rect x="94" y="155" width="8" height="44" rx="3" fill="#5d4037"/>
      <ellipse cx="98" cy="148" rx="28" ry="20" fill="#388e3c"/>
      <ellipse cx="98" cy="144" rx="20" ry="14" fill="#43a047"/>
      <ellipse cx="98" cy="140" rx="14" ry="10" fill="#66bb6a"/>
      
      <rect x="124" y="160" width="7" height="38" rx="3" fill="#5d4037"/>
      <ellipse cx="128" cy="154" rx="22" ry="16" fill="#388e3c"/>
      <ellipse cx="128" cy="150" rx="16" ry="12" fill="#43a047"/>
      
      <ellipse cx="155" cy="205" rx="22" ry="12" fill="#43a047"/>
      <ellipse cx="175" cy="207" rx="18" ry="10" fill="#388e3c"/>
      <ellipse cx="320" cy="207" rx="18" ry="10" fill="#388e3c"/>
      <ellipse cx="342" cy="205" rx="22" ry="12" fill="#43a047"/>
    </svg>
  );
}

// Helper functions
function pwdStrength(v) {
  let score = 0;
  if (v.length >= 8) score++;
  if (/[A-Z]/.test(v)) score++;
  if (/[0-9]/.test(v)) score++;
  if (/[^A-Za-z0-9]/.test(v)) score++;
  return score;
}

const STR_COLORS = ["#ef4444", "#fb923c", "#facc15", "#22c55e"];

// Inline ✅/❌ check — only shown when field has a value
function ValidCheck({ ok, value }) {
  if (!value) return null;
  return (
    <span className="valid-check" style={{ opacity: 1 }}>
      {ok ? "✅" : "❌"}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Signup() {
const { dark, toggle } = useTheme();
  // Form fields
  const [name, setName]       = useState("");
  const [email, setEmail]     = useState("");
  const [reg, setReg]         = useState("");
  const [section, setSection] = useState("");
  const [year, setYear]       = useState("");
  const [pwd, setPwd]         = useState("");
  const [cpwd, setCpwd]       = useState("");
  const [showPwd, setShowPwd]   = useState(false);
  const [showCpwd, setShowCpwd] = useState(false);

  // UI state
  const [shake, setShake]         = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ripple, setRipple]       = useState(null);
  const [toasts, setToasts]       = useState([]);
  const toastCounter              = useRef(0);

  // Per-field error flags — keys match field names
  const [errors, setErrors] = useState({
    name: false, email: false, reg: false,
    section: false, year: false,
    pwd: false, cpwd: false,
  });

  const btnRef = useRef(null);

  // ── Live validation booleans ──────────────────────────────────────────────
  const vName    = name.trim().length > 1;
  const vEmail   = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const vReg     = reg.trim().length > 3;
  const vSection = !!section;
  const vYear    = !!year;
  const vPwd     = pwd.length >= 8;
  const vCpwd    = pwd === cpwd && cpwd.length > 0;

  // Progress bar
  const fields    = [vName, vEmail, vReg, vSection, vYear, vPwd, vCpwd];
  const doneCount = fields.filter(Boolean).length;
  const pct       = Math.round((doneCount / fields.length) * 100);

  // Step indicator
  const g1 = vName && vEmail;
  const g2 = vReg && vSection && vYear;
  const g3 = vPwd && vCpwd;

  const sc1Class = `sc ${g1 ? "done" : "active"}`;
  const sc2Class = `sc ${g2 ? (g1 ? "done" : "active") : g1 ? "active" : ""}`;
  const sc3Class = `sc ${g3 ? "done" : g1 && g2 ? "active" : ""}`;
  const sl1Class = `step-label ${g1 ? "done" : "active"}`;
  const sl2Class = `step-label ${g2 ? (g1 ? "done" : "active") : g1 ? "active" : ""}`;
  const sl3Class = `step-label ${g3 ? "done" : g1 && g2 ? "active" : ""}`;

  const strength = pwdStrength(pwd);

  // ── Toast helpers ─────────────────────────────────────────────────────────
  function pushToast(msg, type = "error", duration = 3200) {
    const id = ++toastCounter.current;
    setToasts((prev) => [...prev, { id, msg, type, exiting: false }]);
    setTimeout(() => {
      setToasts((prev) => prev.map((t) => t.id === id ? { ...t, exiting: true } : t));
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 400);
    }, duration);
  }

  function clearError(field) {
    setErrors((prev) => ({ ...prev, [field]: false }));
  }

  function setError(field) {
    setErrors((prev) => ({ ...prev, [field]: true }));
  }

  function triggerShake() {
    setShake(true);
    setTimeout(() => setShake(false), 400);
  }

  // ── Submit handler ────────────────────────────────────────────────────────
const handleSubmit = async (e) => {
  e.preventDefault();

  // Validation checks
  const newErrors = {
    name:    !vName,
    email:   !vEmail,
    reg:     !vReg,
    section: !vSection,
    year:    !vYear,
    pwd:     !vPwd,
    cpwd:    !vCpwd,
  };

  const failedFields = Object.keys(newErrors).filter(k => newErrors[k]);
  if (failedFields.length > 0) {
    setErrors(newErrors);
    triggerShake();
    pushToast("Please fix the highlighted errors.", "warning");
    return;
  }

  // Ripple effect
  if (btnRef.current) {
    const rect = btnRef.current.getBoundingClientRect();
    const sz = Math.max(rect.width, rect.height) * 2;
    setRipple({ x: sz / 2, y: sz / 2, size: sz });
    setTimeout(() => setRipple(null), 700);
  }

  pushToast("Creating your account…", "info", 1600);

  try {
    const res = await fetch("https://kcg-credits-back.onrender.com/signup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name,
        email,
        reg,
        section,
        year,
        password: pwd,
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      if (data.detail && Array.isArray(data.detail)) {
        data.detail.forEach(err => pushToast(err.msg, "error"));
      } else if (data.detail) {
        pushToast(data.detail, "error");
      } else {
        pushToast("Signup failed.", "error");
      }
      return;
    }

    // Success
    pushToast("🎉 Account created! Redirecting…", "success", 2500);
    setSubmitted(true);
    setTimeout(() => window.location.href = "/login", 2200);
  } catch (err) {
    console.error(err);
    pushToast("Server error. Try again later.", "error");
  }
};

  /*
  function handleSubmit(e) {
    e.preventDefault();

    // Collect all validation failures in one pass
    const newErrors = {
      name:    !vName,
      email:   !vEmail,
      reg:     !vReg,
      dept:    !vDept,
      section: !vSection,
      year:    !vYear,
      pwd:     !vPwd,
      cpwd:    !vCpwd,
    };

    const failedFields = Object.keys(newErrors).filter((k) => newErrors[k]);

    if (failedFields.length > 0) {
      setErrors(newErrors);
      triggerShake();

      // Show the most specific / highest-priority error as the toast
      if (!name.trim()) {
        pushToast("Student name is required.", "warning");
      } else if (!vName) {
        pushToast("Name must be at least 2 characters.", "warning");
      } else if (!email.trim()) {
        pushToast("Email address is required.", "warning");
      } else if (!vEmail) {
        pushToast("Please enter a valid email address.", "error");
      } else if (!reg.trim()) {
        pushToast("Register number is required.", "warning");
      } else if (!vReg) {
        pushToast("Register number must be at least 4 characters.", "warning");
      } else if (!vDept) {
        pushToast("Please select your department.", "warning");
      } else if (!vSection) {
        pushToast("Please select your class section.", "warning");
      } else if (!vYear) {
        pushToast("Please select your academic year.", "warning");
      } else if (!pwd) {
        pushToast("Password is required.", "warning");
      } else if (!vPwd) {
        pushToast("Password must be at least 8 characters.", "error");
      } else if (!cpwd) {
        pushToast("Please confirm your password.", "warning");
      } else if (!vCpwd) {
        pushToast("Passwords do not match.", "error");
      }

      return;
    }

    // Ripple on button
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const sz = Math.max(rect.width, rect.height) * 2;
      setRipple({ x: sz / 2, y: sz / 2, size: sz });
      setTimeout(() => setRipple(null), 700);
    }

    pushToast("Creating your account…", "info", 1600);
    setTimeout(() => {
      pushToast("🎉 Account created! Redirecting…", "success", 2500);
      setSubmitted(true);
      setTimeout(() => (window.location.href = "/login"), 2200);
    }, 1000);
  }*/

  // ── Error styles helpers ──────────────────────────────────────────────────
  const errStyle = { borderColor: "#ef4444", boxShadow: "0 0 0 3px rgba(239,68,68,0.15)" };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div data-theme={dark ? "dark" : "light"} className="signup-page">
      <style>{css}</style>
      
      <Stars />
      <Particles />
      <Clouds />

      <button 
        className="theme-btn" 
        onClick={toggle} 
        aria-label="Toggle theme"
        title={dark ? "Light mode" : "Dark mode"}
      >
        {dark ? "🌙" : "🌞"}
      </button>

      <div className={`page ${shake ? "shake" : ""}`}>
        {/* Logo */}
        <div className="logo-area">
          <div className="logo-arc" />
          <div className="logo-txt">KCGCredits<sup>®</sup></div>
          <div className="tagline">Student Portal</div>
        </div>

        <div className={`card`}>
          <div className="heading">Create Your Account ✨</div>

        {/* Multi-step progress */}
        <div className="prog-wrap">
          <div className="step-labels">
            <span className={sl1Class}>Personal</span>
            <span className={sl2Class}>Academic</span>
            <span className={sl3Class}>Security</span>
          </div>
          <div className="prog-track">
            <div className="prog-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="step-circles">
            <div className="sc-line" />
            <div className={sc1Class}>1</div>
            <div className={sc2Class}>2</div>
            <div className={sc3Class}>3</div>
          </div>
          <div className="pct-badge">{pct}% complete</div>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="fgrid">

            {/* ── Student Name ── */}
            <div className="field full" style={{ animationDelay: "0.22s" }}>
              <label>Student Name</label>
              <div className="iw">
                <span className="i-ico">🎓</span>
                <input
                  type="text"
                  placeholder="Full Name"
                  autoComplete="name"
                  value={name}
                  style={errors.name ? errStyle : name ? { borderColor: vName ? "#22c55e" : "#ef4444" } : {}}
                  onChange={(e) => { setName(e.target.value); clearError("name"); }}
                />
                {errors.name
                  ? <span className="field-err-ico">⚠️</span>
                  : <ValidCheck ok={vName} value={name} />
                }
              </div>
              {errors.name && <span className="field-err-msg">
                {!name.trim() ? "Name is required." : "Name must be at least 2 characters."}
              </span>}
            </div>

            {/* ── Email ── */}
            <div className="field full" style={{ animationDelay: "0.28s" }}>
              <label>Email Address</label>
              <div className="iw">
                <span className="i-ico">📧</span>
                <input
                  type="email"
                  placeholder="student@kcg.edu.in"
                  autoComplete="email"
                  value={email}
                  style={errors.email ? errStyle : email ? { borderColor: vEmail ? "#22c55e" : "#ef4444" } : {}}
                  onChange={(e) => { setEmail(e.target.value); clearError("email"); }}
                />
                {errors.email
                  ? <span className="field-err-ico">⚠️</span>
                  : <ValidCheck ok={vEmail} value={email} />
                }
              </div>
              {errors.email && <span className="field-err-msg">
                {!email.trim() ? "Email is required." : "Please enter a valid email address."}
              </span>}
            </div>

            {/* ── Register No ── */}
            <div className="field" style={{ animationDelay: "0.34s" }}>
              <label>Register No.</label>
              <div className="iw">
                <span className="i-ico">🪪</span>
                <input
                  type="text"
                  placeholder="22BCE0001"
                  value={reg}
                  style={errors.reg ? errStyle : reg ? { borderColor: vReg ? "#22c55e" : "#ef4444" } : {}}
                  onChange={(e) => { setReg(e.target.value); clearError("reg"); }}
                />
                {errors.reg
                  ? <span className="field-err-ico">⚠️</span>
                  : <ValidCheck ok={vReg} value={reg} />
                }
              </div>
              {errors.reg && <span className="field-err-msg">
                {!reg.trim() ? "Register number is required." : "Must be at least 4 characters."}
              </span>}
            </div>

            {/* ── Class Section ── */}
            <div className="field" style={{ animationDelay: "0.42s" }}>
              <label>Class Section</label>
              <div className="iw">
                <span className="i-ico">🏫</span>
                <select
                  value={section}
                  style={errors.section ? errStyle : {}}
                  onChange={(e) => { setSection(e.target.value); clearError("section"); }}
                >
                  <option value="" disabled>Section</option>
                  <option>A</option><option>B</option><option>C</option><option>D</option>
                </select>
                <span className="sel-arr">▾</span>
              </div>
              {errors.section && <span className="field-err-msg">Please select your section.</span>}
            </div>

            {/* ── Academic Year ── */}
            <div className="field" style={{ animationDelay: "0.46s" }}>
              <label>Academic Year</label>
              <div className="iw">
                <span className="i-ico">📅</span>
                <select
                  value={year}
                  style={errors.year ? errStyle : {}}
                  onChange={(e) => { setYear(e.target.value); clearError("year"); }}
                >
                  <option value="" disabled>Year</option>
                  <option>1st Year</option><option>2nd Year</option>
                  <option>3rd Year</option><option>4th Year</option>
                </select>
                <span className="sel-arr">▾</span>
              </div>
              {errors.year && <span className="field-err-msg">Please select your year.</span>}
            </div>

            {/* ── Password ── */}
            <div className="field full" style={{ animationDelay: "0.50s" }}>
              <label>Password</label>
              <div className="iw">
                <span className="i-ico">🔒</span>
                <input
                  type={showPwd ? "text" : "password"}
                  placeholder="Create a strong password"
                  autoComplete="new-password"
                  value={pwd}
                  style={errors.pwd ? errStyle : {}}
                  onChange={(e) => { setPwd(e.target.value); clearError("pwd"); }}
                />
                <button type="button" className="eye-btn" onClick={() => setShowPwd((s) => !s)}>
                  {showPwd ? "🙈" : "👁️"}
                </button>
                {errors.pwd && <span className="field-err-ico" style={{ right: "40px" }}>⚠️</span>}
              </div>
              <div className="str-bars">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="str-bar"
                    style={{ background: i < strength ? STR_COLORS[strength - 1] : "var(--str-empty)" }}
                  />
                ))}
              </div>
              {errors.pwd && <span className="field-err-msg">
                {!pwd ? "Password is required." : "Password must be at least 8 characters."}
              </span>}
            </div>

            {/* ── Confirm Password ── */}
            <div className="field full" style={{ animationDelay: "0.54s" }}>
              <label>Confirm Password</label>
              <div className="iw">
                <span className="i-ico">🔐</span>
                <input
                  type={showCpwd ? "text" : "password"}
                  placeholder="Repeat password"
                  autoComplete="new-password"
                  value={cpwd}
                  style={
                    errors.cpwd
                      ? errStyle
                      : cpwd
                      ? { borderColor: vCpwd ? "#22c55e" : "#ef4444" }
                      : {}
                  }
                  onChange={(e) => { setCpwd(e.target.value); clearError("cpwd"); }}
                />
                <button type="button" className="eye-btn" onClick={() => setShowCpwd((s) => !s)}>
                  {showCpwd ? "🙈" : "👁️"}
                </button>
                {errors.cpwd
                  ? <span className="field-err-ico" style={{ right: "40px" }}>⚠️</span>
                  : <ValidCheck ok={vCpwd} value={cpwd} />
                }
              </div>
              {errors.cpwd && <span className="field-err-msg">
                {!cpwd ? "Please confirm your password." : "Passwords do not match."}
              </span>}
            </div>

          </div>{/* /fgrid */}

          <button
            type="submit"
            className="btn"
            ref={btnRef}
            disabled={submitted}
          >
            {submitted ? "✓ Done!" : "Create Account →"}
            {ripple && (
              <span
                className="ripple"
                style={{
                  width: `${ripple.size}px`,
                  height: `${ripple.size}px`,
                  left: `${ripple.x - ripple.size / 2}px`,
                  top: `${ripple.y - ripple.size / 2}px`,
                }}
              />
            )}
          </button>
        </form>

        <div className="scene">
          {dark ? <Logon /> : <Logo />}
        </div>

        <div className="li-link">
          Already have an account? <a href="/login">Login</a>
        </div>
      </div>

      <ToastStack toasts={toasts} />
      </div>
    </div>
  );
}

// ─── CSS ──────────────────────────────────────────────────────────────────────
const css = `
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');

:root {
  --bg-top: #a8d8f0;
  --bg-bottom: #e4f4ff;
  --card-bg: rgba(255,255,255,0.72);
  --card-border: rgba(255,255,255,0.9);
  --input-bg: rgba(255,255,255,0.82);
  --input-border: rgba(46,125,50,0.22);
  --input-focus: #2e7d32;
  --text-primary: #1a3a1a;
  --text-secondary: #2d7a2d;
  --text-muted: #88aa88;
  --btn-bg: linear-gradient(135deg,#2e7d32,#1b5e20);
  --btn-shadow: rgba(46,125,50,0.3);
  --logo-color: #1a7a1a;
  --logo-arc: #2e7d32;
  --link-color: #1a7a1a;
  --shadow: 0 12px 48px rgba(30,100,30,0.14);
  --toggle-bg: rgba(255,255,255,0.65);
  --toggle-border: rgba(255,255,255,0.9);
  --star-opacity: 0;
  --prog-bg: rgba(46,125,50,0.12);
  --str-empty: rgba(200,200,200,0.2);
}

[data-theme="dark"] {
  --bg-top: #060c18;
  --bg-bottom: #111827;
  --card-bg: rgba(17,24,39,0.82);
  --card-border: rgba(255,255,255,0.07);
  --input-bg: rgba(15,23,42,0.75);
  --input-border: rgba(148,163,184,0.15);
  --input-focus: #818cf8;
  --text-primary: #e2e8f0;
  --text-secondary: #94a3b8;
  --text-muted: #475569;
  --btn-bg: linear-gradient(135deg,#4f46e5,#4338ca);
  --btn-shadow: rgba(79,70,229,0.4);
  --logo-color: #93c5fd;
  --logo-arc: #818cf8;
  --link-color: #818cf8;
  --shadow: 0 12px 48px rgba(0,0,0,0.55);
  --toggle-bg: rgba(255,255,255,0.05);
  --toggle-border: rgba(255,255,255,0.1);
  --star-opacity: 1;
  --prog-bg: rgba(129,140,248,0.12);
  --str-empty: rgba(100,100,140,0.2);
}

*,*::before,*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.signup-page {
  font-family: 'Nunito', sans-serif;
  min-height: 100vh;
  background: linear-gradient(180deg, var(--bg-top) 0%, var(--bg-bottom) 65%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  transition: background 0.7s ease;
}

/* Page container */
.page {
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 40px 20px 20px;
  position: relative;
  z-index: 10;
}

/* Stars */
.stars {
  position: fixed;
  inset: 0;
  pointer-events: none;
  opacity: var(--star-opacity);
  transition: opacity 0.7s;
  z-index: 1;
}

.star {
  position: absolute;
  background: #fff;
  border-radius: 50%;
  animation: twinkle var(--d, 3s) ease-in-out infinite;
  animation-delay: var(--dl, 0s);
}

@keyframes twinkle {
  0%, 100% { opacity: 0.15; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.5); }
}

/* Particles */
.particles {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 1;
}

.particle {
  position: absolute;
  border-radius: 50%;
  animation: float var(--pd, 8s) ease-in-out infinite;
  animation-delay: var(--pdl, 0s);
  opacity: 0.4;
}

@keyframes float {
  0%, 100% { transform: translateY(0) translateX(0); opacity: 0; }
  20%, 80% { opacity: 0.4; }
  50% { transform: translateY(-80px) translateX(30px); opacity: 0.6; }
}

/* Clouds */
.cloud-layer {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  transition: opacity 0.7s;
}

[data-theme="dark"] .cloud-layer {
  opacity: 0.04;
}

.cloud {
  position: absolute;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 50px;
  animation: drift var(--cd, 30s) linear infinite;
  animation-delay: var(--cdl, 0s);
}

.cloud::before,
.cloud::after {
  content: '';
  position: absolute;
  background: inherit;
  border-radius: 50%;
}

.cloud::before {
  width: 60%;
  height: 200%;
  top: -80%;
  left: 15%;
}

.cloud::after {
  width: 40%;
  height: 160%;
  top: -50%;
  right: 10%;
}

@keyframes drift {
  from { transform: translateX(-150px); }
  to { transform: translateX(110vw); }
}

/* Theme toggle */
.theme-btn {
  position: fixed;
  top: 18px;
  right: 18px;
  z-index: 200;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 1.5px solid var(--toggle-border);
  background: var(--toggle-bg);
  backdrop-filter: blur(12px);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  transition: all 0.3s;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.theme-btn:hover {
  transform: scale(1.12) rotate(20deg);
}

/* Card */
.card {
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  background: var(--card-bg);
  border: 1.5px solid var(--card-border);
  border-radius: 32px;
  padding: 28px 32px 24px;
  backdrop-filter: blur(22px);
  box-shadow: var(--shadow);
  animation: slideUp 0.8s 0.15s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  transition: all 0.7s;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--input-focus) transparent;
}

.card::-webkit-scrollbar {
  width: 6px;
}

.card::-webkit-scrollbar-track {
  background: transparent;
}

.card::-webkit-scrollbar-thumb {
  background: var(--input-focus);
  border-radius: 3px;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(40px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

/* Logo */
/* Logo */
.logo-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-40px) scale(0.9); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.logo-arc {
  display: inline-block;
  width: 90px;
  height: 16px;
  border-top: 5px solid var(--logo-arc);
  border-radius: 50% 50% 0 0 / 100% 100% 0 0;
  margin-bottom: -2px;
  transition: border-color 0.7s;
}

.logo-txt {
  font-size: clamp(34px, 8vw, 48px);
  font-weight: 900;
  color: var(--logo-color);
  letter-spacing: -1.5px;
  line-height: 1;
  transition: color 0.7s;
  text-shadow: 0 2px 12px rgba(30, 107, 53, 0.12);
}

.logo-txt sup {
  font-size: 0.38em;
  vertical-align: super;
}

.tagline {
  font-size: 0.75rem;
  font-weight: 800;
  color: var(--text-muted);
  letter-spacing: 2px;
  text-transform: uppercase;
  opacity: 0.65;
  transition: color 0.7s;
}

.heading {
  text-align: center;
  font-size: 1.2rem;
  font-weight: 900;
  color: var(--text-primary);
  margin: 0 0 16px 0;
  transition: color 0.7s;
  animation: fadeUp 0.6s 0.15s both;
}

/* Progress */
.prog-wrap {
  margin-bottom: 18px;
  animation: fadeUp 0.6s 0.2s both;
}

.step-labels {
  display: flex;
  justify-content: space-around;
  gap: 8px;
  margin-bottom: 8px;
}

.step-label {
  font-size: 0.7rem;
  font-weight: 900;
  color: var(--text-muted);
  letter-spacing: 1px;
  text-transform: uppercase;
  opacity: 0.5;
  transition: opacity 0.4s, color 0.4s;
}

.step-label.active {
  opacity: 1;
  color: var(--input-focus);
}

.step-label.done {
  opacity: 0.7;
  color: var(--text-secondary);
}

.prog-track {
  height: 7px;
  background: var(--prog-bg);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 8px;
}

.prog-fill {
  height: 100%;
  border-radius: 10px;
  background: linear-gradient(90deg, var(--input-focus), #86efac);
  transition: width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.step-circles {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  z-index: 1;
}

.sc {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--prog-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: var(--text-secondary);
  font-size: 0.85rem;
  transition: background 0.4s, color 0.4s;
}

.sc.active {
  background: var(--input-focus);
  color: #fff;
  box-shadow: 0 0 12px color-mix(in srgb, var(--input-focus) 40%, transparent);
}

.sc.done {
  background: color-mix(in srgb, var(--input-focus) 55%, transparent);
  color: #fff;
}

.sc-line {
  position: absolute;
  top: 13px;
  left: 13px;
  right: 13px;
  height: 2px;
  background: var(--prog-bg);
  z-index: 0;
}

.pct-badge {
  text-align: center;
  margin-top: 5px;
  font-size: 0.76rem;
  font-weight: 800;
  color: var(--input-focus);
}

form {
  display: contents;
}

.fgrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.full {
  grid-column: 1 / -1;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  animation: fadeUp 0.5s both;
  transition: transform 0.2s;
}

.field:focus-within {
  transform: translateX(4px);
}

label {
  font-size: 0.76rem;
  font-weight: 900;
  color: var(--text-secondary);
  letter-spacing: 0.7px;
  text-transform: uppercase;
  transition: color 0.7s;
}

.iw {
  position: relative;
}

.i-ico {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.95rem;
  pointer-events: none;
  transition: transform 0.3s;
}

.iw:focus-within .i-ico {
  transform: translateY(-50%) scale(1.2);
}

input[type="text"],
input[type="email"],
input[type="password"],
select {
  width: 100%;
  padding: 12px 12px 12px 38px;
  background: var(--input-bg);
  border: 1.5px solid var(--input-border);
  border-radius: 12px;
  font-family: 'Nunito', sans-serif;
  font-size: 0.93rem;
  font-weight: 600;
  color: var(--text-primary);
  outline: none;
  appearance: none;
  transition: border 0.3s, background 0.7s, color 0.7s, box-shadow 0.3s, transform 0.2s;
}

input::placeholder {
  color: var(--text-muted);
  font-weight: 600;
}

input:focus,
select:focus {
  border-color: var(--input-focus);
  box-shadow: 0 0 0 3.5px rgba(45, 138, 78, 0.15);
  transform: translateY(-1px);
}

[data-theme="dark"] input:focus,
[data-theme="dark"] select:focus {
  box-shadow: 0 0 0 3.5px rgba(129, 140, 248, 0.1);
}

.sel-arr {
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 0.75rem;
  color: var(--text-muted);
}

select {
  cursor: pointer;
}

.eye-btn {
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  color: var(--text-muted);
  transition: color 0.2s, transform 0.25s;
  padding: 4px;
}

.eye-btn:hover {
  color: var(--input-focus);
  transform: translateY(-50%) scale(1.18);
}

.valid-check {
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.9rem;
}

.str-bars {
  display: flex;
  gap: 4px;
  margin-top: 6px;
}

.str-bar {
  flex: 1;
  height: 5px;
  border-radius: 5px;
  background: var(--prog-bg);
  transition: background 0.4s;
}

/* Field error */
.field-err-msg {
  font-size: 0.74rem;
  font-weight: 800;
  color: #ef4444;
  margin-top: 2px;
  display: block;
  padding-left: 2px;
  animation: slideDown 0.3s ease both;
}

.field-err-ico {
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.85rem;
  pointer-events: none;
}

/* Button */
.btn {
  width: 100%;
  padding: 14px;
  background: var(--btn-bg);
  color: #fff;
  border: none;
  border-radius: 14px;
  font-family: 'Nunito', sans-serif;
  font-size: 1.02rem;
  font-weight: 900;
  letter-spacing: 0.4px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  margin-top: 18px;
  transition: background 0.3s, transform 0.2s, box-shadow 0.3s;
  box-shadow: 0 5px 20px var(--btn-shadow);
  animation: fadeUp 0.55s 0.55s both;
}

.btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 9px 28px rgba(46, 125, 50, 0.4);
}

[data-theme="dark"] .btn:hover:not(:disabled) {
  box-shadow: 0 9px 28px rgba(79, 70, 229, 0.4);
}

.btn:active:not(:disabled) {
  transform: translateY(0);
}

.btn:disabled {
  opacity: 0.8;
  cursor: default;
}

.ripple {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.38);
  transform: scale(0);
  animation: rip 0.65s linear;
  pointer-events: none;
}

@keyframes rip {
  to {
    transform: scale(4);
    opacity: 0;
  }
}

/* Links */
.li-link {
  text-align: center;
  margin-top: 16px;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-secondary);
  transition: color 0.7s;
  animation: fadeUp 0.55s 0.6s both;
}

.li-link a {
  color: var(--link-color);
  font-weight: 900;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.li-link a:hover {
  opacity: 0.7;
}

/* Toast stack */
.toast-stack {
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  z-index: 999;
  pointer-events: none;
  width: max-content;
  max-width: 90vw;
}

.toast-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 22px 12px 16px;
  border-radius: 50px;
  color: #fff;
  font-weight: 800;
  font-size: 0.92rem;
  box-shadow: 0 6px 28px rgba(0, 0, 0, 0.22);
  backdrop-filter: blur(8px);
  pointer-events: auto;
  min-width: 220px;
  justify-content: center;
}

.toast-enter {
  animation: toastIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.toast-exit {
  animation: toastOut 0.35s ease forwards;
}

.toast-error {
  background: #ef4444;
}

.toast-warning {
  background: #f59e0b;
}

.toast-success {
  background: #22c55e;
}

.toast-info {
  background: #3b82f6;
}

.toast-icon {
  font-size: 1rem;
  flex-shrink: 0;
}

.toast-msg {
  line-height: 1.3;
}

@keyframes toastIn {
  from {
    opacity: 0;
    transform: translateY(24px) scale(0.92);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes toastOut {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(16px) scale(0.9);
  }
}

/* Animations */
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: none; }
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-16px); }
  to { opacity: 1; transform: none; }
}

@keyframes shake {
  0%, 100% { transform: none; }
  20%, 60% { transform: translateX(-7px); }
  40%, 80% { transform: translateX(7px); }
}

.shake {
  animation: shake 0.4s ease;
}

/* Scene */
.scene {
  width: 100%;
  max-width: 500px;
  position: relative;
  animation: slideUp 0.8s 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  margin-top: 12px;
}

/* Responsive */
@media (max-width: 480px) {
  .page {
    padding: 28px 12px 12px;
    gap: 16px;
  }
  .card {
    padding: 20px 16px 16px;
    max-height: 75vh;
  }
  .theme-btn {
    top: 12px;
    right: 12px;
    width: 44px;
    height: 44px;
    font-size: 16px;
  }
  .logo-txt {
    font-size: 2 rem;
  }
  .heading {
    font-size: 1.1rem;
  }
  .fgrid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 360px) {
  .card {
    padding: 18px 13px 16px;
  }
}
`